/**
 * The Step 5 GraphQL documents: search, messaging, issues.
 *
 * A SECOND FILE rather than another 600 lines in queries.js. The split is by
 * step rather than by screen on purpose — `queries.js` is what Steps 1–4
 * shipped and is stable, and a reviewer looking at what Step 5 added should not
 * have to diff a thousand-line file to find it. `queries.js` re-exports
 * everything here, so no screen has to know which file a document lives in.
 */
import { gql } from '@apollo/client';

/* ---------------- search workbench ---------------- */

export const SEARCH_CONTACTS_QUERY = gql`
  query SearchContacts(
    $filter: ContactFilterInput
    $page: Int!
    $pageSize: Int!
    $withFacets: Boolean!
  ) {
    searchContacts(
      filter: $filter
      page: $page
      pageSize: $pageSize
      withFacets: $withFacets
    ) {
      totalCount
      page
      pageSize
      facets {
        kind
        key
        count
      }
      items {
        id
        kind
        displayName
        collaborationStatus
        subscriptionType
        photoUrl
        deletedAt
        messagingOptOut
        interactionCount
        lastInteractionAt
        primaryPhoneE164
        # The default avatar is chosen by gender when there is no photo. The
        # field is already on the contact row and already mapped — the list
        # simply never asked for it.
        gender
        # «معرف», resolved in the CRM, which owns both rows.
        referrerName
        specialty {
          id
          labelFa
        }
        fieldOfWork {
          id
          labelFa
        }
        tags {
          id
          name
          colorHex
          # Which column the chip belongs in — «ارزیابی» or «برچسب‌ها».
          isAssessment
        }
        primaryPhone {
          id
          number
        }
      }
    }
  }
`;

export const SAVED_VIEWS_QUERY = gql`
  query SavedViews {
    savedViews {
      id
      name
      filterJson
      ownerId
      ownerName
      isShared
      isSystem
    }
    bulkLimit
  }
`;

export const CREATE_SAVED_VIEW = gql`
  mutation CreateSavedView($name: String!, $filter: ContactFilterInput!, $isShared: Boolean) {
    createSavedView(name: $name, filter: $filter, isShared: $isShared) {
      id
      name
      filterJson
      isShared
      isSystem
    }
  }
`;

export const UPDATE_SAVED_VIEW = gql`
  mutation UpdateSavedView(
    $id: ID!
    $name: String
    $filter: ContactFilterInput
    $isShared: Boolean
  ) {
    updateSavedView(id: $id, name: $name, filter: $filter, isShared: $isShared) {
      id
      name
      filterJson
      isShared
    }
  }
`;

export const DELETE_SAVED_VIEW = gql`
  mutation DeleteSavedView($id: ID!) {
    deleteSavedView(id: $id)
  }
`;

export const BULK_TAG = gql`
  mutation BulkTag($selector: BulkSelectorInput!, $tagId: ID!) {
    bulkTag(selector: $selector, tagId: $tagId) {
      matched
      affected
    }
  }
`;

export const BULK_UNTAG = gql`
  mutation BulkUntag($selector: BulkSelectorInput!, $tagId: ID!) {
    bulkUntag(selector: $selector, tagId: $tagId) {
      matched
      affected
    }
  }
`;

export const BULK_CHANGE_STATUS = gql`
  mutation BulkChangeStatus(
    $selector: BulkSelectorInput!
    $toStatus: CollaborationStatus!
    $reason: String
  ) {
    bulkChangeStatus(selector: $selector, toStatus: $toStatus, reason: $reason) {
      matched
      affected
    }
  }
`;

export const SET_MESSAGING_OPT_OUT = gql`
  mutation SetMessagingOptOut($contactId: ID!, $optedOut: Boolean!) {
    setMessagingOptOut(contactId: $contactId, optedOut: $optedOut) {
      id
      messagingOptOut
    }
  }
`;

export const REPLAY_PROJECTIONS = gql`
  mutation ReplayProjections {
    replayProjections
  }
`;

/* ---------------- messaging ---------------- */

const MESSAGE_FIELDS = gql`
  fragment MessageFields on Message {
    id
    contactId
    phoneE164
    renderedBody
    channel
    templateId
    status
    suppressionReason
    error
    campaignId
    automationRuleId
    queuedAt
    sendAfter
    sentAt
    deliveredAt
    readAt
    createdAt
    contact {
      id
      displayName
    }
  }
`;

export const MESSAGES_QUERY = gql`
  ${MESSAGE_FIELDS}
  query Messages(
    $page: Int!
    $pageSize: Int!
    $contactId: ID
    $statuses: [MessageStatus!]
    $campaignId: ID
  ) {
    messages(
      page: $page
      pageSize: $pageSize
      contactId: $contactId
      statuses: $statuses
      campaignId: $campaignId
    ) {
      totalCount
      page
      pageSize
      items {
        ...MessageFields
      }
    }
  }
`;

export const SEND_MESSAGE = gql`
  ${MESSAGE_FIELDS}
  mutation SendMessage(
    $contactId: ID!
    $templateId: ID
    $freeBody: String
    $force: Boolean
    $accountId: String
    $media: MediaRefInput
    $channel: String
  ) {
    sendMessage(
      contactId: $contactId
      templateId: $templateId
      freeBody: $freeBody
      force: $force
      accountId: $accountId
      # WHATSAPP (the default when absent) or SMS — declared for the same
      # reason media is below.
      channel: $channel
      # DECLARED. A variable a query does not declare is silently dropped, and
      # the send goes out as its caption alone — the same hole that kept the
      # free-text preview blank.
      media: $media
    ) {
      ...MessageFields
    }
  }
`;

export const RETRY_MESSAGE = gql`
  ${MESSAGE_FIELDS}
  mutation RetryMessage($id: ID!) {
    retryMessage(id: $id) {
      ...MessageFields
    }
  }
`;

export const PREVIEW_SEND_QUERY = gql`
  query PreviewSend(
    $contactId: ID!
    $templateId: ID
    $freeBody: String
    $force: Boolean
    $accountId: String
    $channel: String
  ) {
    previewSend(
      contactId: $contactId
      templateId: $templateId
      # DECLARED, which it was not: the dialog had been passing this as a
      # variable no query named, so every free-text preview rendered blank
      # while templates rendered correctly.
      freeBody: $freeBody
      force: $force
      accountId: $accountId
      channel: $channel
    ) {
      wouldSend
      reason
      detail
      renderedBody
      phoneE164
      accountId
    }
  }
`;

/** The SMS route: whether the send dialog may offer the SMS tab, and whether
 *  what it offers is the sandbox or real texts. */
export const SMS_STATUS_QUERY = gql`
  query SmsStatus {
    smsStatus {
      provider
      configured
      detail
    }
  }
`;

export const WHATSAPP_STATUS_QUERY = gql`
  query WhatsappStatus {
    whatsappStatus {
      provider
      configured
      paired
      connected
      phoneE164
      pushName
      queueDepth
      sentLastHour
      maxPerHour
      sendDisabled
      lastError
      uptimeSeconds
    }
    projectionStatus {
      recipientCount
      sources {
        source
        lastEvent
        lastEventAt
        lastAppliedAt
        appliedCount
      }
    }
  }
`;

export const UNLINK_WHATSAPP = gql`
  mutation UnlinkWhatsapp($accountId: String) {
    unlinkWhatsapp(accountId: $accountId)
  }
`;

/**
 * One of this deployment's numbers.
 *
 * `linked`/`connected` come from the GATEWAY and `label`/`isDefault` from
 * MeshHub, and the fragment keeps both so a screen never has to guess that an
 * unpaired number is merely unconfigured.
 */
export const WHATSAPP_ACCOUNT_FIELDS = gql`
  fragment WhatsappAccountFields on WhatsappAccount {
    id
    accountId
    label
    isDefault
    isActive
    sortOrder
    linked
    connected
    phoneE164
    pushName
    queueDepth
    sentLastHour
    maxPerHour
    sendDisabled
    lastError
    linkedAt
  }
`;

export const WHATSAPP_ACCOUNTS_QUERY = gql`
  query WhatsappAccounts($includeInactive: Boolean) {
    whatsappAccounts(includeInactive: $includeInactive) {
      ...WhatsappAccountFields
    }
  }
  ${WHATSAPP_ACCOUNT_FIELDS}
`;

export const CREATE_WHATSAPP_ACCOUNT = gql`
  mutation CreateWhatsappAccount(
    $label: String!
    $accountId: String
    $isDefault: Boolean
  ) {
    createWhatsappAccount(label: $label, accountId: $accountId, isDefault: $isDefault) {
      ...WhatsappAccountFields
    }
  }
  ${WHATSAPP_ACCOUNT_FIELDS}
`;

export const UPDATE_WHATSAPP_ACCOUNT = gql`
  mutation UpdateWhatsappAccount($id: ID!, $label: String) {
    updateWhatsappAccount(id: $id, label: $label) {
      ...WhatsappAccountFields
    }
  }
  ${WHATSAPP_ACCOUNT_FIELDS}
`;

export const SET_WHATSAPP_ACCOUNT_ACTIVE = gql`
  mutation SetWhatsappAccountActive($id: ID!, $isActive: Boolean!) {
    setWhatsappAccountActive(id: $id, isActive: $isActive) {
      ...WhatsappAccountFields
    }
  }
  ${WHATSAPP_ACCOUNT_FIELDS}
`;

export const SET_DEFAULT_WHATSAPP_ACCOUNT = gql`
  mutation SetDefaultWhatsappAccount($id: ID!) {
    setDefaultWhatsappAccount(id: $id) {
      ...WhatsappAccountFields
    }
  }
  ${WHATSAPP_ACCOUNT_FIELDS}
`;

const CAMPAIGN_FIELDS = gql`
  fragment CampaignFields on Campaign {
    id
    name
    accountId
    channel
    templateId
    freeBody
    recipientSource
    recipientIds
    resolvedCount
    scheduledAt
    state
    queuedCount
    sentCount
    deliveredCount
    readCount
    failedCount
    suppressedCount
    startedAt
    completedAt
    createdAt
  }
`;

export const CAMPAIGNS_QUERY = gql`
  ${CAMPAIGN_FIELDS}
  query Campaigns($page: Int!, $pageSize: Int!) {
    campaigns(page: $page, pageSize: $pageSize) {
      totalCount
      page
      pageSize
      items {
        ...CampaignFields
      }
    }
  }
`;

export const CAMPAIGN_PREVIEW_QUERY = gql`
  query CampaignPreview($id: ID!, $sample: Int) {
    campaignPreview(id: $id, sample: $sample) {
      displayName
      renderedBody
    }
  }
`;

export const CREATE_CAMPAIGN = gql`
  ${CAMPAIGN_FIELDS}
  mutation CreateCampaign(
    $name: String!
    $templateId: ID
    $freeBody: String
    $contactIds: [ID!]
    $filter: ContactFilterInput
    $scheduledAt: String
    $accountId: String
    $media: MediaRefInput
    $channel: String
  ) {
    createCampaign(
      name: $name
      templateId: $templateId
      freeBody: $freeBody
      contactIds: $contactIds
      filter: $filter
      scheduledAt: $scheduledAt
      accountId: $accountId
      media: $media
      channel: $channel
    ) {
      ...CampaignFields
    }
  }
`;

export const SCHEDULE_CAMPAIGN = gql`
  ${CAMPAIGN_FIELDS}
  mutation ScheduleCampaign($id: ID!, $scheduledAt: String) {
    scheduleCampaign(id: $id, scheduledAt: $scheduledAt) {
      ...CampaignFields
    }
  }
`;

export const CANCEL_CAMPAIGN = gql`
  ${CAMPAIGN_FIELDS}
  mutation CancelCampaign($id: ID!) {
    cancelCampaign(id: $id) {
      ...CampaignFields
    }
  }
`;

/* ---------------- automation ---------------- */

export const AUTOMATION_QUERY = gql`
  query Automation($includeDisabled: Boolean) {
    automationRules(includeDisabled: $includeDisabled) {
      id
      name
      trigger
      triggerConfigJson
      templateId
      sendTimeLocal
      enabled
    }
    occasions(includeInactive: true) {
      id
      code
      labelFa
      jalaliMonth
      jalaliDay
      isActive
      isSystem
    }
  }
`;

export const CREATE_AUTOMATION_RULE = gql`
  mutation CreateAutomationRule(
    $name: String!
    $trigger: AutomationTrigger!
    $templateId: ID!
    $triggerConfigJson: String
    $sendTimeLocal: String
  ) {
    createAutomationRule(
      name: $name
      trigger: $trigger
      templateId: $templateId
      triggerConfigJson: $triggerConfigJson
      sendTimeLocal: $sendTimeLocal
    ) {
      id
      name
      trigger
      enabled
    }
  }
`;

export const UPDATE_AUTOMATION_RULE = gql`
  mutation UpdateAutomationRule($id: ID!, $enabled: Boolean, $name: String) {
    updateAutomationRule(id: $id, enabled: $enabled, name: $name) {
      id
      name
      enabled
    }
  }
`;

export const CREATE_OCCASION = gql`
  mutation CreateOccasion(
    $code: String!
    $labelFa: String!
    $jalaliMonth: Int!
    $jalaliDay: Int!
  ) {
    createOccasion(
      code: $code
      labelFa: $labelFa
      jalaliMonth: $jalaliMonth
      jalaliDay: $jalaliDay
    ) {
      id
      code
      labelFa
      jalaliMonth
      jalaliDay
    }
  }
`;

export const AUTOMATION_DRY_RUN_QUERY = gql`
  query AutomationDryRun($localDate: String) {
    automationDryRun(localDate: $localDate) {
      ruleId
      ruleName
      contactId
      displayName
      renderedBody
      reason
    }
  }
`;

/* ---------------- issues ---------------- */

const ISSUE_LIST_FIELDS = gql`
  fragment IssueListFields on Issue {
    id
    kind
    priority
    status
    title
    orphaned
    reportedAt
    contactNotifiedAt
    assigneeId
    assigneeName
    type {
      id
      labelFa
    }
    contact {
      id
      displayName
    }
  }
`;

export const ISSUES_QUERY = gql`
  ${ISSUE_LIST_FIELDS}
  query Issues(
    $page: Int!
    $pageSize: Int!
    $contactId: ID
    $statuses: [IssueStatus!]
    $kinds: [IssueKind!]
    $priorities: [IssuePriority!]
    $assigneeId: ID
    $search: String
  ) {
    issues(
      page: $page
      pageSize: $pageSize
      contactId: $contactId
      statuses: $statuses
      kinds: $kinds
      priorities: $priorities
      assigneeId: $assigneeId
      search: $search
    ) {
      totalCount
      page
      pageSize
      items {
        ...IssueListFields
      }
    }
  }
`;

export const ISSUE_QUERY = gql`
  ${ISSUE_LIST_FIELDS}
  query Issue($id: ID!) {
    issue(id: $id) {
      ...IssueListFields
      description
      dueAt
      resolution
      resolvedAt
      reportedBy
      reportedByName
      comments {
        id
        authorId
        authorName
        body
        isCustomerVisible
        createdAt
        attachments {
          id
          fileName
          mimeType
          sizeBytes
        }
      }
      statusHistory {
        id
        fromStatus
        toStatus
        changedBy
        changedByName
        changedAt
        reason
      }
      links {
        id
        targetType
        targetId
      }
      attachments {
        id
        fileName
        mimeType
        sizeBytes
      }
    }
    issueTypes {
      id
      labelFa
    }
  }
`;

export const ISSUE_TYPES_QUERY = gql`
  query IssueTypes($includeInactive: Boolean) {
    issueTypes(includeInactive: $includeInactive) {
      id
      code
      labelFa
      labelEn
      isActive
      isSystem
      sortOrder
    }
  }
`;

/**
 * The issue-type vocabulary, administered. Same shape as the activity types'
 * (queries.js) — the feedback service mirrors the CRM's lookup contract rather
 * than sharing it, and so does this.
 */
export const CREATE_ISSUE_TYPE = gql`
  mutation CreateIssueType($code: String!, $labelFa: String!, $labelEn: String, $sortOrder: Int) {
    createIssueType(code: $code, labelFa: $labelFa, labelEn: $labelEn, sortOrder: $sortOrder) {
      id
      code
      labelFa
      labelEn
      isActive
      isSystem
    }
  }
`;

export const UPDATE_ISSUE_TYPE = gql`
  mutation UpdateIssueType($id: ID!, $labelFa: String, $labelEn: String, $sortOrder: Int) {
    updateIssueType(id: $id, labelFa: $labelFa, labelEn: $labelEn, sortOrder: $sortOrder) {
      id
      code
      labelFa
      labelEn
      isActive
      isSystem
    }
  }
`;

export const SET_ISSUE_TYPE_ACTIVE = gql`
  mutation SetIssueTypeActive($id: ID!, $isActive: Boolean!) {
    setIssueTypeActive(id: $id, isActive: $isActive) {
      id
      isActive
    }
  }
`;

export const CREATE_ISSUE = gql`
  ${ISSUE_LIST_FIELDS}
  mutation CreateIssue(
    $kind: IssueKind!
    $typeId: ID!
    $title: String!
    $description: String
    $contactId: ID
    $priority: IssuePriority
    $linkTargetType: LinkTarget
    $linkTargetId: ID
  ) {
    createIssue(
      kind: $kind
      typeId: $typeId
      title: $title
      description: $description
      contactId: $contactId
      priority: $priority
      linkTargetType: $linkTargetType
      linkTargetId: $linkTargetId
    ) {
      ...IssueListFields
    }
  }
`;

export const CHANGE_ISSUE_STATUS = gql`
  ${ISSUE_LIST_FIELDS}
  mutation ChangeIssueStatus(
    $id: ID!
    $toStatus: IssueStatus!
    $resolution: String
    $reason: String
  ) {
    changeIssueStatus(id: $id, toStatus: $toStatus, resolution: $resolution, reason: $reason) {
      ...IssueListFields
      resolution
      resolvedAt
    }
  }
`;

export const ASSIGN_ISSUE = gql`
  ${ISSUE_LIST_FIELDS}
  mutation AssignIssue($id: ID!, $assigneeId: ID) {
    assignIssue(id: $id, assigneeId: $assigneeId) {
      ...IssueListFields
    }
  }
`;

export const ADD_ISSUE_COMMENT = gql`
  mutation AddIssueComment($issueId: ID!, $body: String!, $isCustomerVisible: Boolean) {
    addIssueComment(issueId: $issueId, body: $body, isCustomerVisible: $isCustomerVisible) {
      id
      comments {
        id
        authorId
        authorName
        body
        isCustomerVisible
        createdAt
      }
    }
  }
`;

export const NOTIFY_CONTACT_ABOUT_ISSUE = gql`
  mutation NotifyContactAboutIssue($issueId: ID!, $templateId: ID) {
    notifyContactAboutIssue(issueId: $issueId, templateId: $templateId) {
      id
      status
      suppressionReason
      renderedBody
    }
  }
`;

export const ISSUE_STATS_QUERY = gql`
  query IssueStats(
    $dateFrom: String!
    $dateTo: String!
    $groupBy: StatsGroupBy
    $bucket: StatsBucket
  ) {
    issueStats(dateFrom: $dateFrom, dateTo: $dateTo, groupBy: $groupBy, bucket: $bucket) {
      key
      label
      count
      resolvedCount
      reopenedCount
      medianHoursToResolve
      bucketStart
    }
  }
`;

/**
 * The urgent-unassigned alert, for the reminder tray.
 *
 * The feedback service EMITS `feedback.issue.unassigned_urgent` when an URGENT
 * issue has sat without an assignee past the threshold. The tray does not
 * consume that event — the BFF has no subscription transport (todo.md) — it
 * QUERIES the same condition on the same poll the reminders use.
 *
 * That is a deliberate simplification and not a silent one: the event is what
 * makes the escalation a fact other services could act on; this query is what
 * puts it in front of a person today. The threshold differs slightly (the
 * server waits URGENT_UNASSIGNED_HOURS; this shows them as soon as they are
 * unassigned), which errs toward showing work rather than hiding it.
 */
export const URGENT_ISSUES_QUERY = gql`
  query UrgentIssues {
    issues(
      page: 1
      pageSize: 5
      priorities: [URGENT]
      unassigned: true
      statuses: [OPEN, IN_PROGRESS]
    ) {
      totalCount
      items {
        id
        title
        reportedAt
        contact {
          id
          displayName
        }
      }
    }
  }
`;
